// Package imports:
import Bugsnag from '@bugsnag/js';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form, Formik } from 'formik';
import React, { useRef, useState } from 'react';
import * as Yup from 'yup';
// Component imports:
import Button from '../../../ui-elements/Button/Button';
import HeadSection from '../../../ui-elements/HeadSection/HeadSection';
import Checkbox from '../../../ui-elements/Input/Formik/Checkbox';
import Input from '../../../ui-elements/Input/Formik/Input';
import Link from '../../../ui-elements/Link/Link';
import Loading from '../../../ui-elements/Loading/Loading';
import Table from '../../../ui-elements/Table/Table';
import Tooltip from '../../../ui-elements/Tooltip/Tooltip';
import DisplayBox from '../../DisplayBox/DisplayBox';
import HistoryButtonDropDown from '../../HistoryButtonDropDown/HistoryButtonDropDown';
import ExampleReportButton from '../../ExampleReportButton/ExampleReportButton';
// Service imports:
import { useBuyingProcess } from '../../../services/buyhook';
import { GET_KELDAN_API_URL } from '../../../services/config';
import { isSSN, stripSSN } from '../../../services/utils';
import { ErrorMessages } from '../../../services/errorMessages';
// Type imports:
import { IndividualSearch, IndividualSearchResult } from '../../../types/IndividualTypes';
import { IKeldanApiResponse } from '../../../types/KeldanTypes';

interface IPurchaseMembershipInformation {
    ssn: string,
    reference: string | null,
    withAddress: boolean | null
}

interface IProps {
    isAuthenticated:  boolean
}

const MembershipSearch: React.FC<IProps> = ({ isAuthenticated }) => {
    const [ searchResults, setSearchResults] = useState<IndividualSearchResult[]>();
    const [ searchTerm, setSearchTerm] = useState('');
    const searchResultsRef = useRef<HTMLDivElement | null>(null);

    const { tryPurchaseItem, tryToBuyItem, resetState, setToastError, ModalsAndToasts } = useBuyingProcess(async (purchaseMembershipInfo: IPurchaseMembershipInformation, modalReference, modalBool) => {
        const { ssn: rawSsn, reference, withAddress } = purchaseMembershipInfo;
        const ssn = stripSSN(rawSsn);
        try {
            if (!ssn || !isSSN(ssn)) {
                resetState({
                    type: 'alert',
                    headText: 'Ekki gild kennitala'
                });
                return;
            }
            // if submit:
            const requestQuery = new URLSearchParams({
                id: ssn ?? '',
                reference: reference ?? modalReference,
                address: (withAddress ?? modalBool).toString()
            })
            const url = `${GET_KELDAN_API_URL()}/Individual/BuyMembershipOnly`;
            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
                },
                body: requestQuery
            });
            if(response.ok){
                if (response.redirected) {
                    window.location.href = response.url;
                    return;
                }
                const responseBody: IKeldanApiResponse = await response.json();
                const { message } = responseBody;
                if (message === ErrorMessages.NoCard) {
                    resetState('no card');
                }
                else if (message) {
                    resetState({
                        type: 'alert',
                        headText: message
                    });
                } else {
                    resetState('bad request');
                }
            } else {
                resetState('response not ok');
            }
        } catch (e) {
            if (e instanceof Error) Bugsnag.notify(e);
            resetState('network error')
        }
    }, 'hlutafélagaskrá');

    const handleSearch = async (values: ISearchFormValues) => {
        const { name } = values;
        setSearchResults(undefined);
        setSearchTerm(name);
        try {
            const requestQuery = new URLSearchParams({
                term: name
            });
            const url = `${GET_KELDAN_API_URL()}/Individual/Search`;
            const response = await fetch(url, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
            },
            body: requestQuery
            });
            if (response.ok){
                if (response.redirected) {
                    window.location.href = response.url;
                }
                const responseBody: IKeldanApiResponse<IndividualSearch> = await response.json();
                if (responseBody.success) {
                    if(responseBody.result) setSearchResults(responseBody.result.list.list.list);
                    // executeScroll()

                } else {
                    const { message } = responseBody;
                    if (message === ErrorMessages.NoCard) {
                        setToastError('no card');
                    } else if (message) {
                        setToastError({
                            type: 'alert',
                            headText: message
                        });
                    } else {
                        setToastError('bad request');
                    }
                }
            } else {
                setToastError('response not ok');
            }
        } catch (e) {
            if (e instanceof Error) Bugsnag.notify(e);
            setToastError('network error');
        }
    }

    const displayForms = (status: 'enabled' | 'disabled') => {
        const disabled = (status === 'disabled');
        return (
            <div>
                <Formik
                    initialValues={{
                        ssn: '',
                        reference: '',
                        withAddress: false,
                    }}
                    validationSchema={Yup.object({
                        ssn: Yup.string()
                            .required('Kennitala má ekki vera tóm')
                            .min(10, 'Kennitala þarf að innihalda 10 tölustafi')
                            .max(11, 'Kennitala má ekki vera meira en 10 tölustafir')
                            .matches(/^([\d]{6}[-][\d]{4})|^(\d+)$/, 'Kennitala má einungis innihalda tölustafi'),
                        reference: Yup.string()
                    })}
                    onSubmit={(values) => tryPurchaseItem(values)}
                    component={(props: { values: IPurchasingFormValues }) => (
                        <PurchasingSubFormComponent
                            values={props.values}
                            loading={(tryToBuyItem !== null && tryToBuyItem.reference !== null)}
                            disabled={disabled}
                        />
                    )}
                />
                <Formik
                    initialValues={{
                        name: ''
                    }}
                    validationSchema={Yup.object({
                        name: Yup.string().required('Nafn má ekki vera tómt')
                    })}
                    onSubmit={(values) => handleSearch(values)}
                    component={(props: { values: ISearchFormValues }) => (
                        <SearchSubFormComponent
                            values={props.values}
                            loading={(searchResults === undefined && searchTerm.length > 0)}
                            disabled={disabled}
                        />
                    )}
                />
                <ExampleReportButton size='lg' reportType='individual-membership' />
            </div>
        )
    }

    return (
        <div className='main KCL_membership-search'>
            <div className='main__inner-fluid'>
                <div className="shell">
                    <HeadSection
                        noSpaceBetween={!isAuthenticated}
                        title={
                            <>
                                <h1>Aðild - Hlutafélagaþátttaka</h1>
                                {(isAuthenticated)
                                    ? <HistoryButtonDropDown />
                                    : <></>}
                            </>
                        }
                        description={
                            <>
                                <p>Hér getur þú flett upp hlutafélagaþátttöku einstaklinga.</p>
                                <span>
                                    <strong>Með því að slá inn kennitölu er um kaup að ræða.</strong>
                                    <Link style={{marginLeft: '5px'}} linkSize='18' url='/Verdskra#verdskra'>Sjá verðskrá.</Link>
                                </span>
                            </>
                        }
                    />
                    <div>
                        {(isAuthenticated)
                            ? displayForms('enabled')
                            : (
                                <Tooltip
                                    tooltip={
                                        <span>
                                            Þú þarft að skrá þig inn og vera með skráð kreditkort til að nýta þessa þjónustu.
                                        </span>
                                    }
                                    text={displayForms('disabled')}
                                />
                            )
                        }
                    </div>
                </div>
                {(searchTerm.length > 0) && (
                    <div ref={searchResultsRef} className="section--gray">
                        <div className="shell">
                            <div className='title-wrapper'>
                                <h2>Leitarniðurstöður fyrir {searchTerm}</h2>
                                <FontAwesomeIcon onClick={() => setSearchTerm('')} icon={faXmark} className="icon-fa"/>
                            </div>
                            <DisplayBox>
                                {(searchResults === undefined)
                                    ? <Loading />
                                    : (searchResults.length === 0)
                                    ? <p>Engar niðurstöður fundust fyrir leitarorð: {searchTerm}</p>
                                    : <Table
                                        tableSize='lg'
                                        data={searchResults}
                                        columns={[
                                            {
                                                title: 'Nafn',
                                                dataAccessor: ({ name }) => name,
                                                textAlign: 'left',
                                                simpleSortable: ({ name }) => name,
                                            }, {
                                                title: 'Heimili',
                                                dataAccessor: ({ address }) => address,
                                                textAlign: 'left',
                                                simpleSortable: ({ address }) => address
                                            }, {
                                                title: '',
                                                dataAccessor: ({ ssn }) => (
                                                    <Button
                                                        onClick={() => tryPurchaseItem({
                                                            ssn,
                                                            // Null reference tells us to use reference and address from modal.
                                                            reference: null,
                                                            withAddress: null
                                                        }, 'confirm-modal-with-reference-and-addresses')}
                                                        showLoader={(
                                                            tryToBuyItem !== null
                                                            && ssn === tryToBuyItem.ssn
                                                            && tryToBuyItem.reference === null
                                                        )}
                                                        size='sm'
                                                        buttonType='buy'
                                                    >
                                                        Kaupa
                                                    </Button>
                                                )
                                            }
                                        ]}
                                    />
                                }
                            </DisplayBox>
                        </div>
                    </div>
                )}
            </div>
            <ModalsAndToasts />
        </div>
    );
}

export default MembershipSearch;

interface IPurchasingFormValues {
    ssn: string,
    reference: string,
    withAddress: boolean,
}

interface IPurchasingSubFormProps {
    values: IPurchasingFormValues,
    loading: boolean
    disabled: boolean
}

const PurchasingSubFormComponent: React.FC<IPurchasingSubFormProps> = ({
    values,
    loading,
    disabled
}) => {
    return (
        <Form>
            <div className="form__body">
                <div className="form__section">
                    <div className="form__row individualsearchInputWrapper">
                        <div className="searchInput smallInput">
                            <Input
                                label='Kennitala'
                                name="ssn"
                                id="ssn"
                                value={values.ssn}
                                disabled={disabled}
                            />
                        </div>
                        <div className="searchInput">
                            <Input
                                label='Tilvísun á reikning'
                                name="reference"
                                id="reference"
                                value={values.reference}
                                disabled={disabled}
                            />
                        </div>
                        <div className="searchButton">
                            <Button
                                buttonType='buy'
                                showLoader={loading}
                                size="lg"
                                disabled={disabled}
                            >
                                Kaupa
                            </Button>
                        </div>
                    </div>
                    <Checkbox 
                        label="Með heimilisfangi"
                        name="withAddress"
                        id="withAddress"
                        value={values.withAddress.toString()}
                        disabled={disabled}
                    />
                </div>
            </div>
        </Form>
    );
}


interface ISearchFormValues {
    name: string,
}

interface ISearchSubFormProps {
    values: ISearchFormValues,
    loading: boolean
    disabled: boolean
}

const SearchSubFormComponent: React.FC<ISearchSubFormProps> = ({
    values,
    loading,
    disabled
}) => {
    return (
        <Form>
            <div className="form__body">
                <div className="form__section">
                    <p className='nameSearchInfo'>
                        Einnig er hægt að leita eftir nafni:
                    </p>
                    <div className="form__row nameSearchWrapper">
                        <div className="searchInput smallInput">
                            <Input
                                label='Nafn'
                                name="name"
                                id="name"
                                value={values.name}
                                disabled={disabled}
                            />
                        </div>
                        <div className="searchButton">
                            <Button
                                showLoader={loading}
                                size="lg"
                                disabled={disabled}
                            >
                                Leita
                            </Button>
                        </div>
                    </div>
                </div>
            </div>
        </Form>
    );
}

